// Display helpers shared by pages (no imports, so Jest can test them directly).

// Rupiah with the sign in front: -Rp10.000, Rp182.500.
export const rupiah = (n) => (Number(n) < 0 ? "-" : "") + "Rp" + Math.abs(Number(n || 0)).toLocaleString("id-ID");

// "2026-10-03" -> "03/10/2026". Business dates are plain dates (no time zone
// shift), so they are reformatted as text rather than parsed into a Date.
export const tanggalIndo = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
};

// Material quantity from integer milli-units (x1000): 2200 -> "2,2",
// 1250500 -> "1.250,5", -500 -> "-0,5". Never uses floats for the value.
export const qty = (milli) => {
  const n = Math.trunc(Number(milli || 0));
  const a = Math.abs(n);
  const frac = a % 1000 ? "," + String(a % 1000).padStart(3, "0").replace(/0+$/, "") : "";
  return (n < 0 ? "-" : "") + Math.floor(a / 1000).toLocaleString("id-ID") + frac;
};

// F1.5-B: live area hint while typing sizes in cm (the server computes the
// price): 300 x 100 cm, qty 2 -> "6"; 125 x 87 cm, qty 3 -> "3,2625".
export const luasM2 = (pCm, lCm, qtyN = 1) => {
  const p = parseInt(pCm, 10), l = parseInt(lCm, 10), q = parseInt(qtyN, 10) || 0;
  if (!(p > 0 && l > 0 && q > 0)) return null;
  const cm2 = p * l * q;
  const frac = cm2 % 10000 ? "," + String(cm2 % 10000).padStart(4, "0").replace(/0+$/, "") : "";
  return Math.floor(cm2 / 10000).toLocaleString("id-ID") + frac;
};

// Hotfix 2026-10-09 (DTF per meter lari): the cashier types metres ("1,3" or "1.3");
// the API takes whole cm. Empty / invalid -> null. 1,3 -> 130.
export const meterKeCm = (teks) => {
  const s = String(teks ?? "").trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const cm = Math.round(parseFloat(s) * 100);
  return cm > 0 ? cm : null;
};
// whole cm as Indonesian metres: 130 -> "1,3", 150 -> "1,5", 100 -> "1"
export const teksMeter = (cm) => {
  const n = parseInt(cm, 10);
  if (!(n >= 0)) return "";
  const frac = n % 100 ? "," + String(n % 100).padStart(2, "0").replace(/0+$/, "") : "";
  return Math.floor(n / 100).toLocaleString("id-ID") + frac;
};

// hotfix 2026-10-09: a per-metre (meter lari) line reads as its real length, never "1 M2"
export const qtyTeks = (it) => (it?.panjang_nyata_cm != null
  ? `${teksMeter(it.panjang_nyata_cm)} m` + (parseInt(it.quantity, 10) > 1 ? ` (${it.quantity} lembar)` : "")
  : `${it?.quantity ?? ""} ${it?.satuan || ""}`.trim());

// F1.5-F: why an SPK is not in production yet (gates in order of the flow).
export const spkTunggu = (s) => {
  if (!s || s.status_spk !== "DRAFT") return null;
  const t = [];
  if (s.menunggu_acc) t.push("ACC customer");
  if (s.menunggu_dp) t.push("DP");
  if (s.menunggu_stok) t.push("stok bahan");
  return t.length ? `menunggu ${t.join(" + ")}` : null;
};

// owner 2026-10-12: the number staff and customers know is the faktur; old SPKs have only the order number.
export const nomorFaktur = (r) => r?.nomor_faktur || r?.nomor_order || "—";

// owner 2026-10-12 (Monitoring): why a DRAFT SPK is not in production — server list ["ACC","DP","STOK"].
const TERTAHAN = { ACC: "ACC customer", DP: "DP", STOK: "stok bahan" };
export const tertahanTeks = (r) => {
  if (!r || r.status_spk !== "DRAFT") return null;
  const t = (r.tertahan || []).map((k) => TERTAHAN[k] || k);
  return t.length ? `Tertahan: menunggu ${t.join(" + ")}` : "Tertahan: belum Set READY (menu SPK)";
};

// F1.5-K: move a Monday "YYYY-MM-DD" by n weeks (calendar math in UTC, no time zone drift).
export const geserMinggu = (iso, n) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return iso;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3] + 7 * n));
  return d.toISOString().slice(0, 10);
};

// Addendum 2: planned finish — a date, a date + hour (Express in hours), or
// the reason there is none yet (Jersey waits for the ACC, Custom not set).
export const rencanaTeks = (d) => {
  if (!d) return "—";
  if (d.rencana_selesai) return tanggalIndo(String(d.rencana_selesai).slice(0, 10)) + (d.rencana_selesai_jam ? ` ${d.rencana_selesai_jam}` : "");
  return d.rencana_status === "MENUNGGU_ACC" ? "menunggu ACC" : "belum ditentukan";
};

// production time of a transaction: "1 jam", "3 hari", or none
export const waktuProduksiTeks = (w, hari) => (w ? `${w.nilai} ${w.satuan === "JAM" ? "jam" : "hari"}` : hari ? `${hari} hari` : "tanpa waktu default");
